/**
 * useCSRFToken Hook
 *
 * Manages CSRF token fetching and provides the token for API requests.
 * The token is fetched once on initialization and refreshed periodically
 * or when a 403 CSRF error is detected.
 *
 * Usage:
 * 1. Wrap your app with CSRFProvider
 * 2. Use useCSRFToken() hook in components that need the token
 * 3. The axios interceptor will automatically attach the token to requests
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import Logger from '../utils/logger';
import {
  setCSRFToken as setApiClientCSRFToken,
  clearCSRFToken as clearApiClientCSRFToken
} from '../services/apiClient';

// CSRF Token Context
const CSRFContext = createContext(null);

// Configuration
const CSRF_CONFIG = {
  // Token endpoint - use the API base URL since CSRF is handled by the API server
  // The SWA doesn't have this endpoint and will return a 302 redirect to /login
  endpoint: `${
    import.meta.env.VITE_API_BASE_URL ||
    (() => {
      if (import.meta.env.DEV) Logger.warn('[useCSRFToken] VITE_API_BASE_URL not set, using localhost fallback');
      return 'http://localhost:7071/api';
    })()
  }/csrf-token`,
  // Header name for CSRF token
  headerName: 'X-CSRF-Token',
  // Refresh token 5 minutes before expiry
  refreshBufferMs: 5 * 60 * 1000,
  // Retry configuration
  maxRetries: 3,
  retryDelayMs: 1000
};

/**
 * Fetch CSRF token from the server
 * @param {number} retryCount - Current retry attempt
 * @returns {Promise<{token: string, expiresIn: number}>}
 */
async function fetchCSRFToken(retryCount = 0) {
  try {
    const response = await fetch(CSRF_CONFIG.endpoint, {
      method: 'GET',
      credentials: 'include', // Include cookies
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const data = await response.json();
    return {
      token: data.token,
      expiresIn: data.expiresIn || 3600000 // Default 1 hour
    };
  } catch (error) {
    if (retryCount < CSRF_CONFIG.maxRetries) {
      Logger.warn('[CSRF] Token fetch failed, retrying...', {
        attempt: retryCount + 1,
        maxRetries: CSRF_CONFIG.maxRetries,
        error: error.message
      });

      await new Promise(resolve => setTimeout(resolve, CSRF_CONFIG.retryDelayMs * (retryCount + 1)));
      return fetchCSRFToken(retryCount + 1);
    }

    Logger.error('[CSRF] Failed to fetch CSRF token after retries', {
      error: error.message
    });
    throw error;
  }
}

/**
 * CSRF Token Provider Component
 * Provides CSRF token context to child components
 */
export function CSRFProvider({ children }) {
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  /**
   * Refresh the CSRF token
   */
  const refreshToken = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchCSRFToken();
      setToken(result.token);
      setExpiresAt(Date.now() + result.expiresIn);

      // Update apiClient with new token
      setApiClientCSRFToken(result.token);

      Logger.info('[CSRF] Token refreshed successfully');
    } catch (err) {
      setError(err.message);
      Logger.error('[CSRF] Token refresh failed', { error: err.message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Handle 403 CSRF errors by refreshing the token
   */
  const handleCSRFError = useCallback(async () => {
    Logger.warn('[CSRF] CSRF error detected, refreshing token');
    // Clear old token from apiClient
    clearApiClientCSRFToken();
    await refreshToken();
  }, [refreshToken]);

  // Fetch token on mount
  useEffect(() => {
    refreshToken();
  }, [refreshToken]);

  // Set up automatic token refresh before expiry
  useEffect(() => {
    if (!expiresAt) return;

    const timeUntilRefresh = expiresAt - Date.now() - CSRF_CONFIG.refreshBufferMs;

    if (timeUntilRefresh <= 0) {
      // Token is already expired or about to expire
      refreshToken();
      return;
    }

    const timeoutId = setTimeout(() => {
      Logger.info('[CSRF] Proactively refreshing token before expiry');
      refreshToken();
    }, timeUntilRefresh);

    return () => clearTimeout(timeoutId);
  }, [expiresAt, refreshToken]);

  const value = {
    token,
    isLoading,
    error,
    refreshToken,
    handleCSRFError,
    headerName: CSRF_CONFIG.headerName
  };

  return <CSRFContext.Provider value={value}>{children}</CSRFContext.Provider>;
}

CSRFProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access CSRF token context
 * @returns {{
 *   token: string | null,
 *   isLoading: boolean,
 *   error: string | null,
 *   refreshToken: () => Promise<void>,
 *   handleCSRFError: () => Promise<void>,
 *   headerName: string
 * }}
 */
export function useCSRFToken() {
  const context = useContext(CSRFContext);

  if (!context) {
    throw new Error('useCSRFToken must be used within a CSRFProvider');
  }

  return context;
}

/**
 * Get CSRF headers for a request
 * @param {string} token - CSRF token
 * @returns {Object} Headers object with CSRF token
 */
export function getCSRFHeaders(token) {
  if (!token) {
    return {};
  }

  return {
    [CSRF_CONFIG.headerName]: token
  };
}

/**
 * Check if an error is a CSRF validation error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if CSRF error
 */
export function isCSRFError(error) {
  if (!error) return false;

  // Check response status
  if (error.response?.status === 403) {
    const data = error.response?.data;
    if (data?.code === 'CSRF_VALIDATION_FAILED') {
      return true;
    }
    // Also check error message
    if (data?.error?.toLowerCase().includes('csrf')) {
      return true;
    }
  }

  return false;
}

export default useCSRFToken;
